'use client';

import React from 'react';
import { cm } from 'src/utils/classNames';
import { Wallet } from '@drift-labs/icons';
import MarketIcon from '../../Utils/MarketIcon';
import { EarnProductsByAsset } from 'src/hooks/useEarnProduct';
import PointsMono from '@drift-labs/icons/src/icons/components/PointsMono';
import Collapsible from 'src/components/Collapsible/Collapsible';
import { useEarnProductByAssetData } from 'src/hooks/useEarnProductByAssetData';
import { EarnProductDesktop } from './EarnProductDesktop';
import { getColorVar } from 'src/utils/theme/colorVars';
import VariantTag from 'src/components/Tags/VariantTag';
import ChevronButton from 'src/components/Buttons/ChevronButton';

interface Props {
	info: EarnProductsByAsset;
	isLastProduct: boolean;
}

export const EarnProductByAssetDesktop = ({ info, isLastProduct }: Props) => {
	const {
		showAccordion,
		walletIsConnected,
		handleToggleExpanded,
		shouldShowWalletBalance,
		getWalletBalance,
		aprStr,
	} = useEarnProductByAssetData(info);

	return (
		<Collapsible.Root
			className={cm(
				'relative col-span-3 grid grid-cols-subgrid bg-container-bg',
				isLastProduct && 'rounded-b-sm'
			)}
			open={showAccordion}
			onOpenChange={handleToggleExpanded}
		>
			<div
				className={cm(
					'col-span-3 grid grid-cols-subgrid px-2xl py-4.5 hover:cursor-pointer hover:bg-button-disabled',
					isLastProduct && !showAccordion && 'rounded-b-sm',
					showAccordion && 'bg-button-disabled'
				)}
				onClick={handleToggleExpanded}
			>
				<div className="flex items-center gap-lg">
					<MarketIcon
						sizeClass="size-10"
						marketSymbol={info.spotMarket.symbol}
					/>
					<div className="flex flex-col gap-1">
						<div className="flex items-center gap-1 typo-t2 text-text-default">
							{info.spotMarket.symbol}
							{info.isNew && <VariantTag variant="new" />}
						</div>

						{walletIsConnected &&
							shouldShowWalletBalance(info.spotMarket.symbol) && (
								<div className="flex items-center gap-1 text-text-secondary">
									<Wallet size={12} />
									<div className="block typo-b4">
										{getWalletBalance(info.spotMarket.symbol)}
									</div>
								</div>
							)}
					</div>
				</div>

				<div className="flex flex-col items-end justify-center gap-2xs text-end">
					<div className="typo-b4 text-text-secondary">
						{!(
							info.highestApyProductType === 'strategy_vaults' &&
							info.apyToShow > 30
						)
							? 'Up to'
							: ''}
					</div>
					<div className="flex items-center gap-1">
						{info.isPromo && (
							<PointsMono size={16} color={getColorVar('purple-link')} />
						)}
						<div className="typo-t2 text-text-positive-green-button">
							{aprStr}
						</div>
					</div>
				</div>

				<div className="flex items-center justify-end">
					<ChevronButton
						direction={showAccordion ? 'up' : 'down'}
						variant="tertiary"
						size="md"
					/>
				</div>
			</div>

			<Collapsible.Content className="col-span-3 grid grid-cols-subgrid">
				{info.accordionOptions.map((option, optionIndex) => {
					if (option.marketIndex === undefined) {
						return null;
					}
					return (
						<EarnProductDesktop
							key={`option_${optionIndex}_${info.spotMarket.symbol}`}
							earnProduct={option}
							showDetails={false}
							belongsToLastProductRow={isLastProduct}
						/>
					);
				})}
			</Collapsible.Content>
		</Collapsible.Root>
	);
};
